import React, { useMemo, useCallback } from "react";
import { FilterChip } from "../components/FilterChips";
import type { FilterState } from "../../ProjectList/types";
import type { Project } from "@/utils/types";
import { getSmartCondition } from "./useFilterOperations";
import { FilterType, FilterKey, FilterDisplayName } from "@/types/filterEnums";

type FilterValue = string | number;
interface FilterChipConfig {
  readonly filterType: string;
  readonly filterTypeForMenu: string;
  readonly displayName: string;
  readonly getValues: (filters: FilterState) => FilterValue[] | undefined;
  readonly getCondition: (filters: FilterState) => string | undefined;
  readonly getDisplayText: (
    value: FilterValue,
    getFilterDisplayText: (type: string, value: FilterValue) => string,
  ) => string;
  readonly getIcon: (filterType: string) => React.ReactNode;
  readonly getValueIcon: (
    filterType: string,
    value: FilterValue,
  ) => React.ReactNode;
  readonly getValueIcons: (
    filterType: string,
    values: FilterValue[],
  ) => React.ReactNode[];
  readonly onRemove: (
    value: FilterValue,
    onRemoveFilter: (type: FilterKey, value: string) => void,
  ) => void;
  readonly onRemoveAll: () => void;
  readonly onConditionChange: (
    value: FilterValue,
    newCondition: string,
    onUpdateFilter?: (
      type: FilterKey,
      value: FilterValue,
      condition?: string,
    ) => void,
  ) => void;
}

interface UseFilterChipFactoryProps {
  filters: FilterState;
  currentProject: Project | null;
  onRemoveFilter: (filterType: FilterKey, value: string) => void;
  onRemoveEntireFilter: (filterType: FilterKey) => void;
  onUpdateFilter?: (
    filterType: FilterKey,
    value: FilterValue,
    condition?: string,
  ) => void;
  getFilterDisplayText: (filterType: string, value: FilterValue) => string;
  getFilterIcon: (filterType: string) => React.ReactNode;
  getValueIcon: (filterType: string, value: FilterValue) => React.ReactNode;
  getValueIcons: (
    filterType: string,
    values: FilterValue[],
  ) => React.ReactNode[];
  getOrCreateChipRef: (key: string) => React.RefObject<HTMLDivElement>;
  updateFilters?: (filters: Partial<FilterState>) => void;
}

interface FilterMetadata {
  readonly filterType: string;
  readonly filterTypeForMenu: string;
  readonly displayName: string;
  readonly filterKey: keyof FilterState;
  readonly conditionKey?: keyof NonNullable<FilterState["conditions"]>;
  readonly isSpecial?: boolean;
}

const FILTER_METADATA: Record<string, FilterMetadata> = {
  assignee: {
    filterType: FilterDisplayName.ASSIGNEE,
    filterTypeForMenu: FilterType.ASSIGNEE,
    displayName: FilterDisplayName.ASSIGNEE,
    filterKey: FilterKey.USER_IDS,
    conditionKey: FilterKey.USER_IDS,
  },
  status: {
    filterType: FilterDisplayName.STATUS,
    filterTypeForMenu: FilterType.STATUS,
    displayName: FilterDisplayName.STATUS,
    filterKey: FilterKey.STATUSES,
    conditionKey: FilterKey.STATUSES,
  },
  priority: {
    filterType: FilterDisplayName.PRIORITY,
    filterTypeForMenu: FilterType.PRIORITY,
    displayName: FilterDisplayName.PRIORITY,
    filterKey: FilterKey.PRIORITIES,
    conditionKey: FilterKey.PRIORITIES,
  },
  estimate: {
    filterType: FilterDisplayName.ESTIMATE,
    filterTypeForMenu: FilterType.ESTIMATE,
    displayName: FilterDisplayName.ESTIMATE,
    filterKey: FilterKey.ESTIMATES,
    conditionKey: FilterKey.ESTIMATES,
  },
  label: {
    filterType: FilterDisplayName.LABEL,
    filterTypeForMenu: FilterType.LABEL,
    displayName: FilterDisplayName.LABEL,
    filterKey: FilterKey.LABELS,
    conditionKey: FilterKey.LABELS,
  },
  dueDate: {
    filterType: FilterDisplayName.DUE_DATE,
    filterTypeForMenu: FilterType.DUE_DATE,
    displayName: FilterDisplayName.DUE_DATE,
    filterKey: FilterKey.DUE_DATES,
    conditionKey: FilterKey.DUE_DATES,
    isSpecial: true,
  },
} as const;

const createStandardFilterConfig = (
  metadata: FilterMetadata,
  getFilterIcon: (filterType: string) => React.ReactNode,
  getValueIcon: (filterType: string, value: FilterValue) => React.ReactNode,
  getValueIcons: (
    filterType: string,
    values: FilterValue[],
  ) => React.ReactNode[],
  onRemoveFilter: (filterType: FilterKey, value: string) => void,
  onRemoveEntireFilter: (filterType: FilterKey) => void,
  onUpdateFilter?: (
    filterType: FilterKey,
    value: FilterValue,
    condition?: string,
  ) => void,
): FilterChipConfig => ({
  filterType: metadata.filterType,
  filterTypeForMenu: metadata.filterTypeForMenu,
  displayName: metadata.displayName,
  getValues: (filters) => {
    if (!filters || !metadata.filterKey) return undefined;
    const values = filters[metadata.filterKey];
    return Array.isArray(values) && values.length > 0 ? values : undefined;
  },
  getCondition: (filters) =>
    filters && metadata.conditionKey
      ? filters.conditions?.[metadata.conditionKey]
      : undefined,
  getDisplayText: (value, getFilterDisplayText) =>
    String(getFilterDisplayText(metadata.filterTypeForMenu, value)),
  getIcon: getFilterIcon,
  getValueIcon: getValueIcon,
  getValueIcons: getValueIcons,
  onRemove: (value) =>
    onRemoveFilter(metadata.filterKey as FilterKey, String(value)),
  onRemoveAll: () => onRemoveEntireFilter(metadata.filterKey as FilterKey),
  onConditionChange: (value, newCondition) =>
    onUpdateFilter?.(metadata.filterKey as FilterKey, value, newCondition),
});

const createFilterConfigs = (
  getFilterIcon: (filterType: string) => React.ReactNode,
  getValueIcon: (filterType: string, value: FilterValue) => React.ReactNode,
  getValueIcons: (
    filterType: string,
    values: FilterValue[],
  ) => React.ReactNode[],
  onRemoveFilter: (filterType: FilterKey, value: string) => void,
  onRemoveEntireFilter: (filterType: FilterKey) => void,
  onUpdateFilter?: (
    filterType: FilterKey,
    value: FilterValue,
    condition?: string,
  ) => void,
): Record<string, FilterChipConfig> => {
  const configs: Record<string, FilterChipConfig> = {};

  for (const [key, metadata] of Object.entries(FILTER_METADATA)) {
    if (metadata.isSpecial) {
      if (key === "dueDate") {
        configs[key] = {
          filterType: metadata.filterType,
          filterTypeForMenu: metadata.filterTypeForMenu,
          displayName: metadata.displayName,
          getValues: (filters) => {
            if (!filters.dueDates) return undefined;
            return Object.entries(filters.dueDates)
              .filter(([_, value]) => value)
              .map(([key, _]) => key);
          },
          getCondition: (filters) => filters.conditions?.dueDates,
          getDisplayText: (value, getFilterDisplayText) =>
            String(getFilterDisplayText("dueDate", value)),
          getIcon: getFilterIcon,
          getValueIcon: getValueIcon,
          getValueIcons: getValueIcons,
          onRemove: (value) =>
            onRemoveFilter(FilterKey.DUE_DATES, String(value)),
          onRemoveAll: () => onRemoveEntireFilter(FilterKey.DUE_DATES),
          onConditionChange: (value, newCondition) =>
            onUpdateFilter?.(FilterKey.DUE_DATES, value, newCondition),
        };
      }
    } else {
      configs[key] = createStandardFilterConfig(
        metadata,
        getFilterIcon,
        getValueIcon,
        getValueIcons,
        onRemoveFilter,
        onRemoveEntireFilter,
        onUpdateFilter,
      );
    }
  }

  return configs;
};

export const useFilterChipFactory = ({
  filters,
  currentProject,
  onRemoveFilter,
  onRemoveEntireFilter,
  onUpdateFilter,
  getFilterDisplayText,
  getFilterIcon,
  getValueIcon,
  getValueIcons,
  getOrCreateChipRef,
  updateFilters,
}: UseFilterChipFactoryProps) => {
  const filterConfigs = useMemo(
    () =>
      createFilterConfigs(
        getFilterIcon,
        getValueIcon,
        getValueIcons,
        onRemoveFilter,
        onRemoveEntireFilter,
        onUpdateFilter,
      ),
    [
      getFilterIcon,
      getValueIcon,
      getValueIcons,
      onRemoveFilter,
      onRemoveEntireFilter,
      onUpdateFilter,
    ],
  );

  const createFilterChip = useCallback(
    (
      config: FilterChipConfig,
      values: FilterValue[],
      isConsolidated: boolean = false,
    ): JSX.Element => {
      const { filterTypeForMenu, displayName } = config;
      const currentCondition = config.getCondition(filters);
      const smartCondition = getSmartCondition(values, currentCondition);
      const condition = currentCondition || smartCondition;

      const commonProps = {
        filterType: displayName,
        condition,
        icon: config.getIcon(filterTypeForMenu),
        filterTypeForMenu,
        currentProject,
        filters,
        updateFilters,
        onConditionChange: (newCondition: string) =>
          config.onConditionChange(values[0], newCondition, onUpdateFilter),
      };

      if (isConsolidated) {
        const chipKey = `${filterTypeForMenu}-consolidated`;
        return (
          <FilterChip
            key={chipKey}
            ref={getOrCreateChipRef(chipKey)}
            {...commonProps}
            values={values}
            displayText={`${values.length} ${filterTypeForMenu}s`}
            valueIcons={config.getValueIcons(filterTypeForMenu, values)}
            onRemove={config.onRemoveAll}
            isConsolidated={true}
          />
        );
      } else {
        const value = values[0];
        if (value === undefined) {
          return <></>;
        }

        const chipKey = `${filterTypeForMenu}-${value}`;
        return (
          <FilterChip
            key={chipKey}
            ref={getOrCreateChipRef(chipKey)}
            {...commonProps}
            value={value}
            displayText={config.getDisplayText(value, getFilterDisplayText)}
            valueIcon={config.getValueIcon(filterTypeForMenu, value)}
            onRemove={() => config.onRemove(value, onRemoveFilter)}
            values={[value]}
          />
        );
      }
    },
    [
      filters,
      getFilterDisplayText,
      getOrCreateChipRef,
      currentProject,
      onRemoveFilter,
      onUpdateFilter,
      updateFilters,
    ],
  );

  const generateFilterChips = useCallback((): JSX.Element[] => {
    const chips: JSX.Element[] = [];
    const configKeys = Object.keys(filterConfigs);

    for (const key of configKeys) {
      const config = filterConfigs[key];
      if (!config) continue;

      const values = config.getValues(filters);

      if (!values || values.length === 0) continue;

      const isConsolidated = values.length > 1;
      const chip = createFilterChip(config, values, isConsolidated);

      if (chip) {
        chips.push(chip);
      }
    }

    return chips;
  }, [filterConfigs, filters, createFilterChip]);

  return {
    generateFilterChips,
    filterConfigs,
  };
};
